import React from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import { Anchorme } from 'react-anchorme'
import { toUserTimezone, getInitials, getFullName } from 'utils';
import getPlainText from 'utils/mention-to-plaintext';
import { Link } from 'react-router-dom';
import MessageAttachment from "./MessageAttachment";
import IconButton from "@material-ui/core/IconButton";
import {bindMenu, bindTrigger, usePopupState} from "material-ui-popup-state/hooks";
import MoreVertIcon from "@material-ui/icons/MoreHoriz";
import Menu from "@material-ui/core/Menu";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import {useMutation} from "@apollo/react-hooks";
import { UPDATE_USER_ROOM_STATUS } from "graphql/chat";
import moment from "moment";
import TimeAgo from 'react-timeago';


const displayTransform = (id, display) => '@' + display;

const useStyles = makeStyles((theme) => ({
  bubbleSelf: {
    'marginLeft': '10%',
    'marginBottom': 24,
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },
    '& .content': {
      flex: 1,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: 20,
      borderBottomRightRadius: 0,
      padding: theme.spacing(2),
      marginRight: 8,
      whiteSpace: 'pre-wrap'
    },
    '& .header': {
      marginBottom: 4,
    },
    '& a': {
      color: 'white',
      textDecoration: 'underline'
    }
  },
  bubbleOther: {
    'marginRight': '10%',
    'marginBottom': 24,
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
    },
    '& .header': {
      display: 'flex',
      alignItems: 'center',
      marginLeft: theme.spacing(6),
    },
    '& .content': {
      flex: 1,
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.paper,
      borderRadius: 20,
      borderBottomLeftRadius: 0,
      padding: theme.spacing(2),
      marginLeft: 8,
      whiteSpace: 'pre-wrap',
      border: '1px solid',
      borderColor: theme.palette.background['200'],
    },
  },
  ts: {
    marginLeft: theme.spacing(1)
  },
}));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    '&:focus': {
      'backgroundColor': theme.palette.primary.main,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const Message = ({ message, user }) => {
  const classes = useStyles();
  const [updateConvoStatus] = useMutation(UPDATE_USER_ROOM_STATUS);

  const isSelf = user.id === message.user.user_id;
  const isStudent = message.user.user_type === 'student';
  const avatar = (
    <Avatar src={message.user.user_pic}>
      {getInitials(message.user.user_first_name, message.user.user_last_name)}
    </Avatar>
  );
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'messageMenu',
  });

  const markUnread = () => {
    updateConvoStatus({
      variables: {
        userId: user.id,
        roomId: message.room_id,
        timestamp: moment(message.created_at).subtract(1, 's').toJSON(),
      },
    });
    popupState.close();
  };

  return (
    <div className={isSelf ? classes.bubbleSelf : classes.bubbleOther}>
      <div className="header">
        {!isSelf && isStudent && (
          <Link
            className={classes.link}
            to={`/students/${message.user.user_id}/timeline`}
          >
            <Typography
              color="textPrimary"
              variant="subtitle2"
            >
              {getFullName(message.user)}
            </Typography>
          </Link>
        )}
        {!isSelf && !isStudent && (
          <Typography variant="subtitle2">
            {getFullName(message.user)}
          </Typography>
        )}
        {isSelf && <span />}
        <Typography
          className={classes.ts}
          variant="caption"
          color="textSecondary"
        >
          {toUserTimezone(message.created_at, user.timezone).format('MMM D, HH:mm')}
        </Typography>
        {!isSelf && (
          <Box marginLeft="auto">
            <IconButton size="small" {...bindTrigger(popupState)}>
              <MoreVertIcon />
            </IconButton>
            <Menu {...bindMenu(popupState)}>
              <StyledMenuItem onClick={markUnread}>
                <ListItemText primary="Mark Unread" />
              </StyledMenuItem>
            </Menu>
          </Box>
        )}
      </div>
      <Box display="flex" alignItems="flex-end">
        {!isSelf && !isStudent && avatar}
        {!isSelf && isStudent && (
          <Link
            className={classes.link}
            to={`/students/${message.user.user_id}/timeline`}
          >
            {avatar}
          </Link>
        )}
        <div className="content">
          <Typography variant="body1" color="inherit">
            <Anchorme target="_blank" rel="noreferrer noopener">
              {getPlainText(message.message, displayTransform).join('')}
            </Anchorme>
          </Typography>
          { message.attachment && <MessageAttachment fileKey={message.attachment} />}
        </div>
        {isSelf && avatar}
      </Box>
    </div>
  );
};

export default Message;
